import * as React from 'react';

import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import {Box, Button, FormControl, IconButton, ListItemText, MenuItem, Select, Stack, Typography, useTheme} from '@mui/material';
import {alpha} from '@mui/material/styles';
import {
    AggregationSurveyBreakdown,
    AggregationSurveyBreakdownGroup,
    AggregationSurveyBreakdownRow,
    SurveyBreakdownDimension,
} from 'crqlar-react-common/dist/models/aggregation/surveyAggregation.model';
import {getNameForLanguage} from 'crqlar-react-common/dist/utils/translations.util';
import {useTranslation} from 'react-i18next';

import {CardHeadline} from '../../../insights/kit/Headline';
import {NEGATIVE_COLOR, NEUTRAL_COLOR, POSITIVE_COLOR, TARGET_COLOR} from "../../../insights/kit/insightsColors";
import {BAR_DRAW_DURATION, BAR_HEIGHT, BAR_RADIUS, COMPARE_TICK_WIDTH, FLOATING_SHADOW, MARKER_FADE_DELAY, MARKER_FADE_DURATION, MARKER_OVERHANG, MARKER_WIDTH} from "../constants";
import {SeriesCard} from '../../../insights/kit/SeriesCard';
import {signed, useInsightsFormatters} from "../../../insights/kit/insightsFormat";
import {InsightsLegend, LegendEntry} from '../../../insights/kit/InsightsLegend';
import {InsightsLoading} from '../../../insights/kit/insightsLoading';
import {useBarGrowth} from '../base/useBarGrowth';
import {InsightsColumn, InsightsTable} from '../../../insights/kit/InsightsTable';
import {InsightsTooltip, TooltipDelta, TooltipRow, TooltipTitle} from '../../../insights/kit/InsightsTooltip';

const MINIMUM_RESPONSES = 3;

/** Folded down to its ranked rows a group can be three lines, which the 300px plot floor would leave in white. */
const BREAKDOWN_MIN_HEIGHT = 120;

type MetricKey = 'crqlarScore' | 'satisfaction' | 'nps';
type SortKey = 'responses' | MetricKey;

interface SortState {
    key: SortKey;
    direction: 'asc' | 'desc';
}

interface Reference {
    value: number;
}

interface MetricDefinition {
    key: MetricKey;
    labelKey: string;
    min: number;
    max: number;
    diverging: boolean;
}

const METRICS: MetricDefinition[] = [
    {key: 'crqlarScore', labelKey: 'Crqlar score', min: 0, max: 100, diverging: false},
    {key: 'satisfaction', labelKey: 'Satisfaction', min: 0, max: 100, diverging: false},
    {key: 'nps', labelKey: 'NPS', min: -100, max: 100, diverging: true},
];

const DIMENSION_LABEL_KEYS: Record<SurveyBreakdownDimension, string> = {
    [SurveyBreakdownDimension.SERVICE]: 'Service',
    [SurveyBreakdownDimension.RESOURCE_CATEGORY]: 'Resource',
    [SurveyBreakdownDimension.SECTOR]: 'Sector',
    [SurveyBreakdownDimension.LANGUAGE]: 'Language',
    [SurveyBreakdownDimension.QUESTION]: 'Question',
};

interface PreparedRow {
    id: string;
    name: string;
    responses: number;
    compareResponses: number | null;
    values: Record<MetricKey, number | null>;
    compare: Record<MetricKey, number | null>;
    ranked: boolean;
    verdictColor: Record<MetricKey, string>;
    children?: PreparedRow[];
}

interface DisplayRow {
    row: PreparedRow;
    depth: number;
}

interface BreakdownCardProps {
    breakdown?: AggregationSurveyBreakdown;
    loading?: InsightsLoading;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const groupKey = (group: AggregationSurveyBreakdownGroup) => `${group.dimension}:${group.id ?? ''}`;

interface MetricBarProps {
    metric: MetricDefinition;
    value: number | null;
    reference: Reference | null;
    compare: number | null;
    color: string;
    grown: boolean;
    name: string;
    label: string;
    labels: {thisPeriod: string; compare: string; target: string};
    format: (value: number) => string;
}

function MetricBar({metric, value, reference, compare, color, grown, name, label, labels, format}: MetricBarProps) {
    const toPercent = (raw: number) =>
        ((clamp(raw, metric.min, metric.max) - metric.min) / (metric.max - metric.min)) * 100;

    const base = toPercent(0);
    const point = value == null ? base : toPercent(value);

    const left = Math.min(base, point);
    const width = Math.abs(point - base);

    const panel = (
        <React.Fragment>
            <TooltipTitle title={name} subtitle={label}/>
            <TooltipRow swatch={color} label={labels.thisPeriod} value={value == null ? '—' : format(value)}/>
            {compare != null && <TooltipRow swatch={'text.disabled'} label={labels.compare} value={format(compare)}/>}
            {reference != null && (
                <TooltipRow swatch={TARGET_COLOR} label={labels.target} value={format(reference.value)}/>
            )}
        </React.Fragment>
    );

    return (
        <InsightsTooltip followCursor placement={'top'} title={panel}>
            <Box sx={{position: 'relative', height: BAR_HEIGHT, minWidth: 0}}>
                <Box
                    sx={theme => ({
                        position: 'absolute',
                        inset: 0,
                        borderRadius: `${BAR_RADIUS}px`,
                        bgcolor: alpha(theme.palette.text.primary, 0.05),
                    })}
                />

                {metric.diverging && (
                    <Box
                        sx={theme => ({
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            left: `${base}%`,
                            width: '1px',
                            bgcolor: alpha(theme.palette.text.primary, 0.25),
                        })}
                    />
                )}

                {value != null && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            left: `${grown ? left : base}%`,
                            width: `${grown ? width : 0}%`,
                            borderRadius: `${BAR_RADIUS}px`,
                            bgcolor: color,
                            transition: `width ${BAR_DRAW_DURATION}ms ease-out, left ${BAR_DRAW_DURATION}ms ease-out`,
                        }}
                    />
                )}

                {compare != null && (
                    <Box
                        sx={theme => ({
                            position: 'absolute',
                            top: 2,
                            left: `${toPercent(compare)}%`,
                            ml: `${-COMPARE_TICK_WIDTH / 2}px`,
                            width: `${COMPARE_TICK_WIDTH}px`,
                            height: BAR_HEIGHT - 4,
                            borderRadius: '1px',
                            bgcolor: alpha(theme.palette.text.primary, 0.45),
                            '@keyframes breakdownTickFadeIn': {from: {opacity: 0}, to: {opacity: 1}},
                            animation: `breakdownTickFadeIn ${MARKER_FADE_DURATION}ms ease-out ${MARKER_FADE_DELAY}ms both`,
                        })}
                    />
                )}

                {reference != null && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -MARKER_OVERHANG,
                            left: `${toPercent(reference.value)}%`,
                            ml: `${-MARKER_WIDTH / 2}px`,
                            width: `${MARKER_WIDTH}px`,
                            height: BAR_HEIGHT + MARKER_OVERHANG * 2,
                            borderRadius: '2px',
                            bgcolor: TARGET_COLOR,
                            '@keyframes breakdownTickFadeIn': {from: {opacity: 0}, to: {opacity: 1}},
                            animation: `breakdownTickFadeIn ${MARKER_FADE_DURATION}ms ease-out ${MARKER_FADE_DELAY}ms both`,
                        }}
                    />
                )}
            </Box>
        </InsightsTooltip>
    );
}

interface SortableHeaderProps {
    label: string;
    active: boolean;
    direction: 'asc' | 'desc';
    onClick: () => void;
}

function SortableHeader({label, active, direction, onClick}: SortableHeaderProps) {
    const Arrow = direction === 'desc' ? ArrowDownwardRoundedIcon : ArrowUpwardRoundedIcon;

    return (
        <Box sx={{minWidth: 0}}>
            <Box
                component={'button'}
                type={'button'}
                onClick={onClick}
                aria-sort={active ? (direction === 'desc' ? 'descending' : 'ascending') : 'none'}
                sx={theme => ({
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.25,
                    p: 0,
                    border: 'none',
                    bgcolor: 'transparent',
                    color: active ? 'text.primary' : 'text.secondary',
                    fontFamily: 'inherit',
                    fontSize: 13,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    transition: 'color 140ms ease',
                    '&:hover': {color: 'text.primary'},
                    '&:focus-visible': {
                        outline: `2px solid ${alpha(theme.palette.primary.main, 0.5)}`,
                        outlineOffset: 2,
                    },
                })}
            >
                {label}
                <Arrow sx={{fontSize: 14, opacity: active ? 1 : 0}}/>
            </Box>
        </Box>
    );
}

interface BreakdownTableProps {
    rows: DisplayRow[];
    metrics: MetricDefinition[];
    labels: {name: string; responses: string; was: string};
    metricLabels: Record<MetricKey, string>;
    hasCompare: boolean;
    formatCount: (value: number) => string;
    formatMetric: (metric: MetricKey, value: number) => string;
}

function BreakdownTable({rows, metrics, labels, metricLabels, hasCompare, formatCount, formatMetric}: BreakdownTableProps) {
    const columns: Array<InsightsColumn<DisplayRow> | false> = [
        {
            key: 'name',
            kind: 'label',
            label: labels.name,
            cell: ({row, depth}) => <Box component={'span'} sx={{pl: depth > 0 ? 4 : 0}}>{row.name}</Box>,
        },

        {key: 'responses', kind: 'detail', label: labels.responses, cell: ({row}) => row.responses, format: formatCount},

        ...metrics.flatMap<InsightsColumn<DisplayRow> | false>(metric => [
            {
                key: metric.key,
                kind: 'verdict',
                label: metricLabels[metric.key],
                cell: ({row}) => row.values[metric.key],
                format: value => formatMetric(metric.key, value),
                color: ({row}) => (row.ranked ? row.verdictColor[metric.key] : 'text.secondary'),
            },
            hasCompare && {
                key: `${metric.key}-compare`,
                kind: 'compare',
                label: labels.was,
                cell: ({row}) => row.compare[metric.key],
                format: value => formatMetric(metric.key, value),
            },
        ]),
    ];

    return <InsightsTable label={labels.name} columns={columns} rows={rows} rowKey={({row}) => row.id}/>;
}

export function BreakdownCard({breakdown, loading}: BreakdownCardProps) {
    const unjudged = useTheme().palette.primary.main;
    const {t, i18n} = useTranslation();

    const [picked, setPicked] = React.useState<string | null>(null);
    const [sort, setSort] = React.useState<SortState>({key: 'crqlarScore', direction: 'desc'});
    const [expanded, setExpanded] = React.useState<ReadonlySet<string>>(() => new Set());
    // The group whose rows under the minimum are shown; switching groups folds them away again.
    const [unfoldedGroup, setUnfoldedGroup] = React.useState<string | null>(null);

    const toggleExpanded = (id: string) =>
        setExpanded(current => {
            const next = new Set(current);

            if (next.has(id)) next.delete(id);
            else next.add(id);

            return next;
        });

    // Questions are ranked on Evaluation. Only offer splits with rows in the current or compare period.
    const groups = React.useMemo(
        () => (breakdown?.groups ?? []).filter(group =>
            group.dimension !== SurveyBreakdownDimension.QUESTION && group.rows.length > 0),
        [breakdown],
    );
    const hasCompare = breakdown?.compareRange != null;
    const grown = useBarGrowth(hasCompare);

    const format = useInsightsFormatters();
    const formatCount = format.count;

    const formatMetric = React.useCallback(
        (metric: MetricKey, value: number) => {
            if (metric === 'satisfaction') return `${format.whole(value)} %`;
            if (metric === 'nps') return signed(value, format.whole).text;

            return format.whole(value);
        },
        [format],
    );

    const metricLabels = React.useMemo<Record<MetricKey, string>>(
        () => ({crqlarScore: t('Crqlar score'), satisfaction: t('Satisfaction'), nps: t('NPS')}),
        [t],
    );

    const barLabels = React.useMemo(
        () => ({
            thisPeriod: t('This period'),
            compare: t('Compare period'),
            target: t('Target'),
        }),
        [t],
    );

    const groupLabel = React.useCallback(
        (group: AggregationSurveyBreakdownGroup) =>
            group.dimension === SurveyBreakdownDimension.RESOURCE_CATEGORY && group.name
                ? group.name
                : t(DIMENSION_LABEL_KEYS[group.dimension]),
        [t],
    );

    const active = React.useMemo(() => {
        const pickedGroup = picked == null ? undefined : groups.find(group => groupKey(group) === picked);

        return pickedGroup ?? groups[0];
    }, [groups, picked]);

    const references = React.useMemo<Record<MetricKey, Reference | null>>(() => {
        const referenceFor = (key: MetricKey): Reference | null => {
            const target = active?.targets?.[key];
            return target == null ? null : {value: target};
        };

        return {
            crqlarScore: referenceFor('crqlarScore'),
            satisfaction: referenceFor('satisfaction'),
            nps: referenceFor('nps'),
        };
    }, [active]);

    const columnLabels = metricLabels;

    const nameOf = React.useCallback(
        (row: AggregationSurveyBreakdownRow, dimension: SurveyBreakdownDimension) => {
            if (dimension === SurveyBreakdownDimension.LANGUAGE) {
                return getNameForLanguage(row.name, true) || row.name.toUpperCase();
            }

            const localized = row.localizedName;
            if (localized != null) {
                return localized[i18n.language] ?? localized['en'] ?? Object.values(localized)[0] ?? row.name;
            }

            return row.name;
        },
        [i18n.language],
    );

    const rows = React.useMemo<PreparedRow[]>(() => {
        if (active == null) return [];

        const order = (left: PreparedRow, right: PreparedRow) => {
            if (left.ranked !== right.ranked) return Number(right.ranked) - Number(left.ranked);

            const value = (row: PreparedRow) => (sort.key === 'responses' ? row.responses : row.values[sort.key]);
            const leftValue = value(left);
            const rightValue = value(right);

            if (leftValue == null || rightValue == null) {
                return Number(leftValue == null) - Number(rightValue == null);
            }

            const difference = rightValue - leftValue;
            const ordered = sort.direction === 'desc' ? difference : -difference;

            return ordered !== 0 ? ordered : left.name.localeCompare(right.name);
        };

        const prepare = (row: AggregationSurveyBreakdownRow): PreparedRow => {
            const ranked = row.responses >= MINIMUM_RESPONSES;

            const values: Record<MetricKey, number | null> = {
                crqlarScore: row.crqlarScore,
                satisfaction: row.satisfaction,
                nps: row.nps,
            };

            const verdict = (key: MetricKey) => {
                const value = values[key];
                const reference = references[key];

                // Too thin to rank is a different statement from nothing to rank against: grey says the
                // figure cannot be judged, the default colour says nothing has asked it to be.
                if (!ranked || value == null) return NEUTRAL_COLOR;
                if (reference == null) return unjudged;

                return value >= reference.value ? POSITIVE_COLOR : NEGATIVE_COLOR;
            };

            return {
                id: row.id,
                name: nameOf(row, active.dimension),
                responses: row.responses,
                compareResponses: row.compareResponses,
                values,
                compare: {
                    crqlarScore: row.compareCrqlarScore,
                    satisfaction: row.compareSatisfaction,
                    nps: row.compareNps,
                },
                ranked,
                verdictColor: {
                    crqlarScore: verdict('crqlarScore'),
                    satisfaction: verdict('satisfaction'),
                    nps: verdict('nps'),
                },
                children: row.children?.map(prepare).sort(order),
            };
        };

        return active.rows.map(prepare).sort(order);
    }, [active, nameOf, references, sort, unjudged]);

    const hasHierarchy = rows.some(row => (row.children?.length ?? 0) > 0);

    const metrics = React.useMemo(() => {
        const carries = (row: PreparedRow, key: MetricKey): boolean =>
            row.values[key] != null || (row.children ?? []).some(child => carries(child, key));

        return METRICS.filter(metric => rows.some(row => carries(row, metric.key)));
    }, [rows]);

    const unmeasured = METRICS.filter(metric => !metrics.includes(metric));
    const anyRanked = rows.some(row => row.ranked);

    const displayRows = React.useMemo<DisplayRow[]>(
        () =>
            rows.flatMap(row =>
                expanded.has(row.id) && (row.children?.length ?? 0) > 0
                    ? [{row, depth: 0}, ...(row.children ?? []).map(child => ({row: child, depth: 1}))]
                    : [{row, depth: 0}],
            ),
        [expanded, rows],
    );

    /*
     * Rows under the minimum are folded away below the ranked ones, behind one button - they cannot be judged, and
     * a service booked once read as noise between the ones that can. With nothing ranked, there is nothing to fold.
     */
    const unranked = rows.filter(row => !row.ranked).length;
    const unfolded = active != null && unfoldedGroup === groupKey(active);
    const folds = anyRanked && unranked > 0;

    const chartRows = React.useMemo<DisplayRow[]>(() => {
        if (!folds || unfolded) return displayRows;

        const shown = new Set(rows.filter(row => row.ranked).map(row => row.id));
        let parentShown = false;

        return displayRows.filter(({row, depth}) => {
            if (depth === 0) parentShown = shown.has(row.id);
            return parentShown;
        });
    }, [displayRows, folds, rows, unfolded]);

    const responses = breakdown?.summary.responses ?? 0;
    const previousResponses = breakdown?.summary.previousResponses ?? null;
    const days = breakdown?.range.days ?? null;
    const compareDays = breakdown?.compareRange?.days ?? null;
    const sameLength = days != null && compareDays != null && days === compareDays;

    const perDay = (value: number, over: number) => value / over;

    const delta =
        previousResponses == null || days == null || compareDays == null
            ? null
            : sameLength
                ? responses - previousResponses
                : perDay(responses, days) - perDay(previousResponses, compareDays);

    const formatChange = sameLength ? formatCount : format.decimal;

    const headline = (
        <CardHeadline
            value={formatCount(responses)}
            suffix={t('responses')}
            change={delta == null || previousResponses == null || days == null || compareDays == null ? null : {
                title: t('Responses'),
                subtitle: sameLength ? undefined : t('Per day, as the two ranges differ in length'),
                current: sameLength ? formatCount(responses) : formatChange(perDay(responses, days)),
                previous: sameLength ? formatCount(previousResponses) : formatChange(perDay(previousResponses, compareDays)),
                delta,
                formatDelta: formatChange,
            }}
        />
    );

    const toggleSort = (key: SortKey) =>
        setSort(current =>
            current.key === key
                ? {key, direction: current.direction === 'desc' ? 'asc' : 'desc'}
                : {key, direction: 'desc'},
        );

    const tableLabels = React.useMemo(
        () => ({
            name: active == null ? t('Name') : groupLabel(active),
            responses: t('Responses'),
            was: t('was'),
        }),
        [active, groupLabel, t],
    );

    const anyTarget = metrics.some(metric => references[metric.key] != null);

    const legend = (
        <InsightsLegend>
            {anyRanked && anyTarget && (
                <React.Fragment>
                    <LegendEntry shape={'bar'} color={POSITIVE_COLOR} label={t('At or above target')}/>
                    <LegendEntry shape={'bar'} color={NEGATIVE_COLOR} label={t('Below target')}/>
                </React.Fragment>
            )}

            {anyTarget && <LegendEntry shape={'tick'} color={TARGET_COLOR} width={MARKER_WIDTH} label={barLabels.target}/>}

            {hasCompare && <LegendEntry shape={'tick'} color={alpha(NEUTRAL_COLOR, 0.9)} width={COMPARE_TICK_WIDTH} label={barLabels.compare}/>}

            {unmeasured.length > 0 && (
                <Typography sx={{color: 'text.disabled', fontSize: 14, fontWeight: 600, lineHeight: '20px'}} noWrap>
                    {t('No {{metrics}} question in this survey', {
                        metrics: unmeasured.map(metric => columnLabels[metric.key]).join(` ${t('or')} `),
                    })}
                </Typography>
            )}
        </InsightsLegend>
    );

    const gridTemplate = `minmax(150px, 1.5fr) 44px ${metrics.map(() => 'minmax(130px, 1fr) 56px').join(' ')}`;

    const empty = rows.length > 0 && metrics.length > 0 ? undefined : (
        <Typography variant={'body2'} color={'text.secondary'}>
            {responses === 0
                ? t('No responses in this period.')
                : active == null
                    ? t('No data')
                    : t('Nothing to score by {{group}} in this period.', {group: groupLabel(active)})}
        </Typography>
    );

    // In the header from sm up; a phone's header has no room left beside the title, so there the select sits above
    // the rows.
    const splitPicker = (placement: 'header' | 'toolbar') => groups.length > 0 && active != null && (
        <FormControl
            size={'small'}
            fullWidth={placement === 'toolbar'}
            sx={placement === 'header'
                ? {display: {xs: 'none', sm: 'inline-flex'}, minWidth: 180, maxWidth: 280}
                : {display: {xs: 'flex', sm: 'none'}, mb: 2}}
        >
            <Select<string>
                value={groupKey(active)}
                onChange={event => setPicked(event.target.value)}
                renderValue={() => `${t('Split by')}: ${groupLabel(active)}`}
                inputProps={{'aria-label': t('Split by')}}
                sx={{height: 36, '& .MuiSelect-select': {py: 0.75}}}
                MenuProps={{PaperProps: {variant: 'outlined', sx: {maxHeight: 360, boxShadow: FLOATING_SHADOW}}}}
            >
                {groups.map(group => (
                    <MenuItem key={groupKey(group)} value={groupKey(group)}>
                        <ListItemText primary={groupLabel(group)}/>

                        <Box
                            component={'span'}
                            sx={{ml: 3, fontSize: 12, fontWeight: 600, color: 'text.secondary', fontVariantNumeric: 'tabular-nums'}}
                        >
                            {formatCount(group.rows.length)}
                        </Box>
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    const table = (
        <BreakdownTable
            rows={displayRows}
            metrics={metrics}
            labels={tableLabels}
            metricLabels={columnLabels}
            hasCompare={hasCompare}
            formatCount={formatCount}
            formatMetric={formatMetric}
        />
    );

    // The rows' hover background reaches 8px past their text on both sides; the scroll box carries those 8px as its
    // own padding, or they count as overflow and a horizontal scrollbar shows on every group.
    const chart = (
        <Box sx={{overflowX: 'auto', mx: -1, px: 1}}>
            <Box sx={{minWidth: 720}}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: gridTemplate,
                        alignItems: 'start',
                        columnGap: 1.5,
                        pb: 1,
                        mb: 0.5,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Typography sx={{color: 'text.secondary', fontSize: 13, fontWeight: 600}} noWrap>
                        {tableLabels.name}
                    </Typography>

                    <SortableHeader
                        label={'n'}
                        active={sort.key === 'responses'}
                        direction={sort.direction}
                        onClick={() => toggleSort('responses')}
                    />

                    {metrics.map(metric => (
                        <React.Fragment key={metric.key}>
                            <SortableHeader
                                label={columnLabels[metric.key]}
                                active={sort.key === metric.key}
                                direction={sort.direction}
                                onClick={() => toggleSort(metric.key)}
                            />

                            <Box/>
                        </React.Fragment>
                    ))}
                </Box>

                <Stack>
                    {chartRows.map(({row, depth}) => {
                        const children = row.children?.length ?? 0;
                        const isExpanded = expanded.has(row.id);

                        return (
                            <Box
                                key={row.id}
                                sx={theme => ({
                                    display: 'grid',
                                    gridTemplateColumns: gridTemplate,
                                    alignItems: 'center',
                                    columnGap: 1.5,
                                    px: 1,
                                    mx: -1,
                                    py: 0.75,
                                    borderRadius: 1.5,
                                    transition: 'background-color 120ms ease',
                                    '&:hover': {backgroundColor: alpha(theme.palette.text.primary, 0.03)},
                                })}
                            >
                                <Stack direction={'row'} alignItems={'center'} spacing={0.25} sx={{minWidth: 0, pl: depth * 2.5}}>
                                    {children > 0 ? (
                                        <IconButton
                                            size={'small'}
                                            aria-label={`${isExpanded ? t('Hide') : t('Show')} ${row.name}`}
                                            aria-expanded={isExpanded}
                                            onClick={() => toggleExpanded(row.id)}
                                            sx={{p: 0.25, ml: -0.5, color: 'text.secondary', flexShrink: 0}}
                                        >
                                            <ChevronRightRoundedIcon
                                                sx={{
                                                    fontSize: 18,
                                                    transition: 'transform 140ms ease',
                                                    transform: isExpanded ? 'rotate(90deg)' : 'none',
                                                }}
                                            />
                                        </IconButton>
                                    ) : (
                                        hasHierarchy && depth === 0 && <Box sx={{width: 22, flexShrink: 0}}/>
                                    )}

                                    <Typography
                                        sx={{
                                            minWidth: 0,
                                            fontSize: depth > 0 ? 13 : 14,
                                            fontWeight: depth > 0 ? 500 : 600,
                                            color: depth > 0 ? 'text.secondary' : 'text.primary',
                                        }}
                                        noWrap
                                        title={row.name}
                                    >
                                        {row.name}
                                    </Typography>
                                </Stack>

                                <Typography sx={{color: 'text.secondary', fontSize: 13, fontVariantNumeric: 'tabular-nums'}}>
                                    {formatCount(row.responses)}
                                </Typography>

                                {metrics.map(metric => {
                                    const value = row.values[metric.key];
                                    const compare = row.compare[metric.key];

                                    return (
                                        <React.Fragment key={metric.key}>
                                            <MetricBar
                                                metric={metric}
                                                value={value}
                                                reference={references[metric.key]}
                                                compare={compare}
                                                color={row.verdictColor[metric.key]}
                                                grown={grown}
                                                name={row.name}
                                                label={columnLabels[metric.key]}
                                                labels={barLabels}
                                                format={raw => formatMetric(metric.key, raw)}
                                            />

                                            <Box sx={{textAlign: 'right', minWidth: 0}}>
                                                <Typography
                                                    sx={{
                                                        fontSize: 14,
                                                        fontWeight: 700,
                                                        fontVariantNumeric: 'tabular-nums',
                                                        color: value == null ? 'text.disabled' : row.ranked ? 'text.primary' : 'text.secondary',
                                                    }}
                                                >
                                                    {value == null ? '—' : formatMetric(metric.key, value)}
                                                </Typography>

                                                {compare != null && (
                                                    <Typography sx={{color: 'text.disabled', fontSize: 12, fontVariantNumeric: 'tabular-nums'}}>
                                                        {`${tableLabels.was} ${formatMetric(metric.key, compare)}`}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </React.Fragment>
                                    );
                                })}
                            </Box>
                        );
                    })}
                </Stack>

                {folds && active != null && (
                    <Button
                        size={'small'}
                        color={'inherit'}
                        onClick={() => setUnfoldedGroup(unfolded ? null : groupKey(active))}
                        sx={{mt: 1, color: 'text.secondary', textTransform: 'none'}}
                    >
                        {unfolded
                            ? t('Hide unranked rows', {count: unranked, minimum: MINIMUM_RESPONSES})
                            : t('Show unranked rows', {count: unranked, minimum: MINIMUM_RESPONSES})}
                    </Button>
                )}
            </Box>
        </Box>
    );

    return (
        <SeriesCard
            title={t('Breakdown')}
            hint={t('The survey split one way at a time, with the scores from the overview as columns so a row reads across; a score the survey has no question for is left out. The amber tick on each bar is the target from the survey\'s target set, so a bar reads as above or below it without a second number; a column without a target carries no tick and no colour. Rows with too few responses are shown but not ranked.')}
            headline={headline}
            loading={loading}
            actions={splitPicker('header')}
            toolbar={splitPicker('toolbar')}
            legend={legend}
            empty={empty}
            chart={chart}
            table={table}
            chartMinHeight={BREAKDOWN_MIN_HEIGHT}
        />
    );
}
